import { ESMStockListItemFE, ESMStockFilterOptions, ESMTransactionType } from '@/types/esmStock';
import { apiClient } from './client';

/**
 * Fetch available filter options for ESM stocks
 */
export const getESMStockFilterOptions = async (): Promise<ESMStockFilterOptions> => {
  // Get default dates (today and 30 days ago)
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  
  const end_date = today.toISOString().split('T')[0]; // YYYY-MM-DD format
  const start_date = thirtyDaysAgo.toISOString().split('T')[0];

  // These are the default values
  return {
    stock_name: '',
    start_date,
    end_date,
    transaction_type: ESMTransactionType.entry
  };
};

/**
 * Fetch ESM stocks based on filter criteria
 */
export const getESMStocks = async (filters: Record<string, unknown> = {}): Promise<ESMStockListItemFE[]> => {
  try {
    const response = await apiClient.get('/market/esm-stocks-list', {
      params: filters
    });
    
    // Type assertion for response structure
    const responseData = response.data as { data: ESMStockListItemFE[] };
    
    // Ensure we have a valid response structure
    if (!responseData.data) {
      console.warn('Unexpected API response structure:', responseData);
      return [];
    }
    
    return responseData.data || [];
  } catch (error) {
    console.error('Error fetching ESM stocks:', error);
    throw error;
  }
};

interface ESMDataPoint {
  esm_state: number;
  transition_date: string;
  transaction_type: string;
}

interface CandlestickDataPoint {
  trade_date: string;
  open_price: number;
  high_price: number;
  low_price: number;
  close_price: number;
  total_traded_qty: number;
  delivery_qty: number;
  number_of_trades: number;
  esm_data: ESMDataPoint | null;
}

/**
 * Interface for ESM Stock Detail API response
 */
export interface ESMStockDetailResponse {
  stock_details: {
    id: number;
    isin: string;
    stock_name: string;
    nse_code: string;
    bse_code: string;
    stock_code: string;
    industry_name: string;
    sector_name: string;
    created_at: string;
    updated_at: string;
  };
  stock_financial_data: {
    id: number;
    isin: string;
    current_price: number;
    price_to_earning: number;
    market_capitalization: number;
    promoter_holding: number;
    created_at: string;
    updated_at: string;
  };
  esm_history_list: {
    isin: string;
    current_esm_state: number;
    transaction_type: string;
    transition_date: string;
  }[];
  chart_data: CandlestickDataPoint[];
  prediction_data?: {
    prediction_type: string;
    predicted_transition_date: string;
    reason: string;
    days_spent_in_current_state: number;
    days_completed: number;
    days_spent_in_esm: number;
    min_days_required: number;
  } | null;
}

/**
 * Fetch detailed information for a specific ESM stock by ISIN
 */
export const getESMStockDetail = async (isin: string): Promise<ESMStockDetailResponse> => {
  try {
    const response = await apiClient.get('/market/esm-stock-detail', {
      params: { isin }
    });
    
    // Type assertion for response structure
    const responseData = response.data as { data: ESMStockDetailResponse };
    
    // Ensure we have a valid response structure
    if (!responseData.data) {
      console.warn('Unexpected API response structure:', responseData);
      throw new Error('Invalid API response structure');
    }
    
    return responseData.data;
  } catch (error) {
    console.error(`Error fetching ESM stock detail for ISIN ${isin}:`, error);
    throw error;
  }
}; 